import React, {
  useEffect,
  useState,
} from "react";

import styles from "./ExtentProfile.module.css";

import {
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";

/* =========================================================
   API CONFIG
========================================================= */

const API_BASE_URL =
  "https://coinyoco.world/brobizee-api";

const API_SINGLE_PARTNER =
  `${API_BASE_URL}/get_single_partner.php`;

/* =========================================================
   DEFAULT AVATAR
========================================================= */

const DEFAULT_AVATAR =
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=300&q=80";

/* =========================================================
   HELPERS
========================================================= */

const safeString = (value, fallback = "") => {
  if (value === null || value === undefined) {
    return fallback;
  }
  return String(value).trim() || fallback;
};

const safeNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const getProfileImage = (profile) => {
  const image = safeString(profile, "");

  if (!image) return DEFAULT_AVATAR;

  if (
    image.startsWith("http://") ||
    image.startsWith("https://") ||
    image.startsWith("data:image/")
  ) {
    return image;
  }

  if (image.startsWith("/")) {
    return `${API_BASE_URL}${image}`;
  }

  return `${API_BASE_URL}/${image}`;
};

const formatExperience = (value) => {
  const experience = safeString(value, "");
  if (!experience) return "Experience not added";
  if (/year|yr|years|yrs/i.test(experience)) return experience;
  return `${experience} Yrs`;
};

const formatAge = (value) => {
  const age = safeString(value, "");
  if (!age) return "Age N/A";
  if (/year|yr|years|yrs/i.test(age)) return age;
  return `${age} Yrs`;
};

const formatTeamSize = (partner) => {
  const helperList = safeString(partner.helper_list, "");
  if (!helperList) return "1 Person";

  const match = helperList.match(/\d+/);
  if (match) {
    const count = Number(match[0]);
    if (count > 0) return `${count}+ Team`;
  }
  return "Team";
};

const formatPrice = (partner) => {
  const candidates = [
    partner.money,
    partner.charges,
    partner.charge,
    partner.price,
    partner.hourly_rate,
    partner.rate,
  ];

  const found = candidates.find(
    (v) =>
      v !== null &&
      v !== undefined &&
      String(v).trim() !== ""
  );

  return found !== undefined ? `${found}` : "Contact";
};

/* =========================================================
   MAP LINK BUILDER
========================================================= */

const buildMapSrc = (partner) => {
  const latLong = safeString(
    partner.latitude_longitude,
    ""
  );

  if (!latLong) {
    /* Fallback — a default location */
    return `https://maps.google.com/maps?q=-7.930,112.600&z=15&output=embed`;
  }

  /* "19.0760,72.8777" → already usable */
  const cleaned = latLong.replace(/[()[\]]/g, "").trim();

  return `https://maps.google.com/maps?q=${encodeURIComponent(
    cleaned
  )}&z=15&output=embed`;
};

/* =========================================================
   COMPONENT
========================================================= */

export default function ExtentProfile({
  onBack,
  onCall,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  /* URL params: /:userId/:deviceUuid/partner/:partnerId */
  const {
    userId,
    deviceUuid,
    partnerId,
  } = useParams();

  const basePath =
    userId && deviceUuid
      ? `/${userId}/${deviceUuid}`
      : "";

  /* Fallback data passed via navigate state */
  const statePartner =
    location.state?.partner || null;

  /* =====================================================
     STATE
  ===================================================== */

  const [partner, setPartner] = useState(
    statePartner
  );

  const [isSaved, setIsSaved] = useState(false);

  const [loading, setLoading] = useState(
    !statePartner
  );

  const [error, setError] = useState("");

  /* =====================================================
     FETCH PARTNER FROM API
  ===================================================== */

  useEffect(() => {
    let cancelled = false;

    /* If partnerId missing in URL */
    if (!partnerId) {
      /* Fall back to state data if available */
      if (statePartner) {
        setPartner(statePartner);
        setLoading(false);
        return undefined;
      }

      setError("Partner ID missing in URL.");
      setLoading(false);
      return undefined;
    }

    const fetchPartner = async () => {
      try {
        setLoading(true);
        setError("");

        const url =
          `${API_SINGLE_PARTNER}?partner_id=` +
          encodeURIComponent(partnerId);

        const response = await fetch(url, {
          method: "GET",
          headers: { Accept: "application/json" },
          cache: "no-store",
        });

        if (!response.ok) {
          throw new Error(
            `HTTP ${response.status}`
          );
        }

        const result = await response.json();

        if (cancelled) return;

        if (
          !result ||
          result.success !== true ||
          !result.partner
        ) {
          throw new Error(
            result?.message ||
              "Partner data not found."
          );
        }

        setPartner(result.partner);
      } catch (err) {
        console.error(
          "Single Partner Fetch Error:",
          err
        );

        if (cancelled) return;

        /* If we already have statePartner, use it */
        if (statePartner) {
          setPartner(statePartner);
        } else {
          setError(
            err?.message ||
              "Unable to load partner profile."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPartner();

    return () => {
      cancelled = true;
    };
  }, [partnerId]);

  /* =====================================================
     BACK
  ===================================================== */

  const handleBack = () => {
    if (typeof onBack === "function") {
      onBack();
    } else {
      navigate(-1);
    }
  };

  /* =====================================================
     CALL
  ===================================================== */

  const handleCall = () => {
    if (typeof onCall === "function") {
      onCall(partner?.phone_number);
      return;
    }

    const phone = safeString(
      partner?.phone_number,
      ""
    );

    if (!phone) {
      alert(
        "Partner phone number available nahi hai."
      );
      return;
    }

    window.location.href = `tel:${phone}`;
  };

  /* =====================================================
     SAVE TOGGLE
  ===================================================== */

  const toggleSave = () => {
    setIsSaved((prev) => !prev);
  };

  /* =====================================================
     LOADING UI
  ===================================================== */

  if (loading) {
    return (
      <div className={styles.screenWrapper}>
        <div className={styles.deviceFrame}>
          <header className={styles.appBar}>
            <button
              type="button"
              className={styles.backBtn}
              onClick={handleBack}
            >
              <svg
                width="20"
                height="16"
                viewBox="0 0 24 16"
                fill="none"
                stroke="#005CA9"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 8H5" />
                <polyline points="12 1 5 8 12 15" />
              </svg>
            </button>

            <div className={styles.headerCenter}>
              <div className={styles.headerDot}></div>
              <h1 className={styles.headerTitle}>
                Profile
              </h1>
            </div>

            <span />
          </header>

          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: "14px",
              padding: "30px",
            }}
          >
            <div
              style={{
                width: "46px",
                height: "46px",
                borderRadius: "50%",
                border:
                  "4px solid #e6eef6",
                borderTop:
                  "4px solid #005CA9",
                animation:
                  "extentSpin 0.9s linear infinite",
              }}
            />

            <p
              style={{
                color: "#555",
                fontSize: "13px",
                fontWeight: 600,
              }}
            >
              Loading partner profile...
            </p>

            <style>{`
              @keyframes extentSpin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      </div>
    );
  }

  /* =====================================================
     ERROR UI
  ===================================================== */

  if (error) {
    return (
      <div className={styles.screenWrapper}>
        <div className={styles.deviceFrame}>
          <header className={styles.appBar}>
            <button
              type="button"
              className={styles.backBtn}
              onClick={handleBack}
            >
              <svg
                width="20"
                height="16"
                viewBox="0 0 24 16"
                fill="none"
                stroke="#005CA9"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 8H5" />
                <polyline points="12 1 5 8 12 15" />
              </svg>
            </button>

            <div className={styles.headerCenter}>
              <div className={styles.headerDot}></div>
              <h1 className={styles.headerTitle}>
                Profile
              </h1>
            </div>

            <span />
          </header>

          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: "10px",
              padding: "30px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "42px" }}>
              ⚠️
            </div>

            <h3
              style={{
                margin: 0,
                fontSize: "16px",
                color: "#222",
              }}
            >
              Unable to Load Profile
            </h3>

            <p
              style={{
                margin: 0,
                fontSize: "12px",
                color: "#666",
              }}
            >
              {error}
            </p>

            <button
              type="button"
              onClick={() =>
                window.location.reload()
              }
              style={{
                marginTop: "10px",
                border: "none",
                background: "#0066cc",
                color: "#fff",
                padding: "10px 20px",
                borderRadius: "10px",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* =====================================================
     NO PARTNER
  ===================================================== */

  if (!partner) {
    return (
      <div className={styles.screenWrapper}>
        <div className={styles.deviceFrame}>
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: "10px",
              padding: "30px",
            }}
          >
            <h3>No partner data found</h3>
          </div>
        </div>
      </div>
    );
  }

  /* =====================================================
     DERIVED VALUES FROM PARTNER
  ===================================================== */

  const partnerName = safeString(
    partner.full_name,
    "Partner"
  );

  const partnerAvatar = getProfileImage(
    partner.profile
  );

  const partnerCategory = safeString(
    partner.work,
    "Service Provider"
  );

  const secondaryWork = safeString(
    partner.secondary_work,
    ""
  );

  const skillsText =
    [secondaryWork, partnerCategory]
      .filter(Boolean)
      .join(", ") || "Professional Service";

  const experienceText = formatExperience(
    partner.experiences
  );

  const ageText = formatAge(partner.age);

  const teamSize = formatTeamSize(partner);

  const price = formatPrice(partner);

  const workingCharges = safeString(
    partner.working_charges,
    ""
  );

  const mode = safeString(
    partner.mode,
    "2TW"
  );

  const likes = safeNumber(partner.likes, 0);

  const views = safeString(
    partner.views,
    "0"
  );

  const rating = safeString(
    partner.rating,
    "4.5"
  );

  const bio = safeString(
    partner.bio,
    "The service provider has not added a bio yet."
  );

  const recentlyWork = safeString(
    partner.recently_work,
    ""
  );

  const address = safeString(
    partner.address,
    ""
  );

  const onlineStatus = safeString(
    partner.online_stutes,
    ""
  ).toLowerCase();

  const isOnline =
    onlineStatus === "online" ||
    onlineStatus === "on" ||
    onlineStatus === "active";

  const mapSrc = buildMapSrc(partner);

  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <div className={styles.screenWrapper}>
      <div className={styles.deviceFrame}>

        {/* ================= HEADER ================= */}
        <header className={styles.appBar}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={handleBack}
            aria-label="Go Back"
          >
            <svg
              width="20"
              height="16"
              viewBox="0 0 24 16"
              fill="none"
              stroke="#005CA9"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 8H5" />
              <polyline points="12 1 5 8 12 15" />
            </svg>
          </button>

          <div className={styles.headerCenter}>
            <div className={styles.headerDot}></div>
            <h1 className={styles.headerTitle}>
              Profile
            </h1>
          </div>

          <button
            type="button"
            className={`${styles.saveBtn} ${
              isSaved ? styles.savedActive : ""
            }`}
            onClick={toggleSave}
            aria-label={
              isSaved
                ? "Remove from saved"
                : "Save Profile"
            }
          >
            <span className={styles.saveIconWrap}>
              <svg
                width="21"
                height="21"
                viewBox="0 0 24 24"
                fill={isSaved ? "#FF6B00" : "none"}
                stroke="#FF6B00"
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            </span>

            <span className={styles.saveLabel}>
              {isSaved ? "Saved" : "Save"}
            </span>
          </button>
        </header>

        {/* ================= MAP (FIXED) ================= */}
        <section className={styles.mapSection}>
          <div className={styles.mapGlow}></div>

          <iframe
            title="Profile Map Location"
            className={styles.mapIframe}
            src={mapSrc}
            loading="lazy"
          ></iframe>

          <div className={styles.mapOverlay}></div>

          <div className={styles.locationPill}>
            <span className={styles.locationDot}></span>
            <span>Service Location</span>
          </div>

          <div className={styles.mapZoomBadge}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 10c0 7-9 12-9 12S3 17 3 10a9 9 0 1 1 18 0Z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <span>Nearby</span>
          </div>
        </section>

        {/* ================= SCROLLABLE CARD ================= */}
        <main className={styles.scrollContainer}>
          <section className={styles.profileCard}>

            <div className={styles.cardHandle}></div>

            {/* ================= PROFILE HEADER ================= */}
            <div className={styles.cardHeader}>

              <div className={styles.avatarWrapper}>
                <div className={styles.avatarRing}>
                  <img
                    src={partnerAvatar}
                    alt={partnerName}
                    className={styles.avatarImg}
                    onError={(e) => {
                      e.currentTarget.src =
                        DEFAULT_AVATAR;
                    }}
                  />
                </div>

                {isOnline && (
                  <span className={styles.onlineBadge}>
                    <span></span>
                  </span>
                )}
              </div>

              <div className={styles.headerInfo}>

                <div className={styles.badgeRow}>
                  <span className={styles.assuredBadge}>
                    <span className={styles.assuredIcon}>
                      ✓
                    </span>
                    BroBizee
                    <span className={styles.subTag}>
                      Assured
                    </span>
                  </span>

                  <span className={styles.categoryBadge}>
                    {partnerCategory}
                  </span>

                  {partner.account_type && (
                    <span className={styles.tierTag}>
                      {safeString(
                        partner.account_type,
                        ""
                      ).toUpperCase()}
                    </span>
                  )}
                </div>

                <div className={styles.nameRow}>
                  <h2 className={styles.profileName}>
                    {partnerName}
                  </h2>

                  <div className={styles.ratingBadge}>
                    <span className={styles.ratingNum}>
                      {rating}
                    </span>
                    <span className={styles.starIcon}>
                      ★
                    </span>
                  </div>
                </div>

                <div className={styles.metaRow}>
                  <span className={styles.metaText}>
                    <span className={styles.metaIcon}>
                      👥
                    </span>
                    {teamSize}
                  </span>

                  <span className={styles.viewsCount}>
                    <span>Views</span>
                    <strong>{views}</strong>
                  </span>
                </div>

                <div className={styles.metaSubRow}>
                  <span className={styles.metaText}>
                    <strong>Exp.</strong>{" "}
                    {experienceText}
                  </span>

                  <span className={styles.metaText}>
                    <span className={styles.metaIcon}>
                      👤
                    </span>
                    {ageText}
                  </span>
                </div>

              </div>
            </div>

            {/* ================= QUICK STATS ================= */}
            <div className={styles.quickStats}>

              <div className={styles.statItem}>
                <span className={styles.statIcon}>
                  ₹
                </span>
                <div>
                  <small>Starting From</small>
                  <strong>{price}</strong>
                </div>
              </div>

              <div className={styles.statDivider} />

              <div className={styles.statItem}>
                <span className={styles.statIcon}>
                  🚲
                </span>
                <div>
                  <small>Mode</small>
                  <strong>{mode}</strong>
                </div>
              </div>

              <div className={styles.statDivider} />

              <div className={styles.statItem}>
                <span className={styles.statIcon}>
                  👍
                </span>
                <div>
                  <small>Likes</small>
                  <strong>{likes}</strong>
                </div>
              </div>

            </div>

            {/* ================= SKILLS ================= */}
            <div className={styles.infoCard}>
              <div className={styles.infoTitleRow}>
                <div className={styles.infoTitleIcon}>
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.2-3.2a6 6 0 0 1-7.6 7.6l-6.6 6.6a2.12 2.12 0 0 1-3-3l6.6-6.6a6 6 0 0 1 7.6-7.6l-3.2 3.2Z" />
                  </svg>
                </div>

                <span>Skills</span>
              </div>

              <p className={styles.valueText}>
                {skillsText}
              </p>
            </div>

            {/* ================= RECENT WORK ================= */}
            {recentlyWork && (
              <div className={styles.workCard}>
                <div className={styles.workTopRow}>
                  <div className={styles.infoTitleRow}>
                    <div className={styles.infoTitleIcon}>
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect
                          width="20"
                          height="14"
                          x="2"
                          y="7"
                          rx="2"
                          ry="2"
                        />
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                      </svg>
                    </div>

                    <span>Recently Worked At</span>
                  </div>
                </div>

                <p className={styles.workCompany}>
                  {recentlyWork}
                </p>
              </div>
            )}

            {/* ================= BIO ================= */}
            <div className={styles.bioSection}>
              <div className={styles.bioHeaderRow}>
                <div>
                  <span className={styles.sectionEyebrow}>
                    ABOUT PROFESSIONAL
                  </span>

                  <h3 className={styles.sectionTitle}>
                    Bio
                  </h3>
                </div>

                <button
                  type="button"
                  className={styles.callNowBtn}
                  onClick={handleCall}
                  disabled={!partner.phone_number}
                >
                  <span className={styles.callIcon}>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 1 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  </span>

                  <span>
                    {partner.phone_number
                      ? "Call Now"
                      : "No Number"}
                  </span>
                </button>
              </div>

              <p className={styles.bioContent}>
                {bio}
              </p>
            </div>

            {/* ================= ADDRESS ================= */}
            {address && (
              <div className={styles.infoCard}>
                <div className={styles.infoTitleRow}>
                  <div className={styles.infoTitleIcon}>
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 10c0 7-9 12-9 12S3 17 3 10a9 9 0 1 1 18 0Z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>

                  <span>Address</span>
                </div>

                <p className={styles.valueText}>
                  {address}
                </p>
              </div>
            )}

            {/* ================= FINANCIAL INFO ================= */}
            <div className={styles.infoCard}>
              <div className={styles.infoTitleRow}>
                <div className={styles.infoTitleIcon}>
                  ₹
                </div>

                <span>Charges</span>
              </div>

              <p className={styles.valueText}>
                Money: ₹{" "}
                {safeString(partner.money, "—")}
                {"  •  "}
                Working Charges:{" "}
                {workingCharges || "—"}
              </p>
            </div>

            {/* ================= PHOTOS — If Any ================= */}
            {(partner.upload_documents_images1 ||
              partner.upload_documents_images2) && (
              <div className={styles.gallerySection}>
                <div className={styles.galleryHeader}>
                  <div>
                    <span
                      className={styles.sectionEyebrow}
                    >
                      WORK SHOWCASE
                    </span>

                    <h3 className={styles.sectionTitle}>
                      Photos
                    </h3>
                  </div>
                </div>

                <div className={styles.galleryGrid}>
                  {partner.upload_documents_images1 && (
                    <div
                      className={styles.mainGalleryImg}
                    >
                      <img
                        src={getProfileImage(
                          partner.upload_documents_images1
                        )}
                        alt="Work"
                      />
                    </div>
                  )}

                  {partner.upload_documents_images2 && (
                    <div
                      className={styles.mainGalleryImg}
                      style={{ marginTop: "8px" }}
                    >
                      <img
                        src={getProfileImage(
                          partner.upload_documents_images2
                        )}
                        alt="Work"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

          </section>
        </main>
      </div>
    </div>
  );
}